<template>
  <transition name="slide" mode="out-in">
    <div v-if="isVisible" id="alert-banner" data-test-alert>
      <div class="container">
        <div class="flex-container align-justify align-top">
          <div class="alert-banner__content flex-container">
            <span class="alert-banner__icon icon-info" />
            <div>
              <h4 data-test-alert-title class="alert-banner__title" v-html="alert.title" />
              <div class="alert-banner__body-container flex-container">
                <p data-test-alert-body class="alert-banner__body" v-html="alert.body" />
                <a
                  v-if="alert.link"
                  data-test-alert-link
                  :href="alert.link.url"
                  class="alert-banner__link"
                  v-html="alert.link.text"
                />
              </div>
            </div>
          </div>
          <button
            data-test-alert-close-button
            class="alert-banner__close-button icon icon-close"
            @click="closeAlert(alert.id)"
          />
        </div>
      </div>
    </div>
  </transition>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { AlertModel } from './types/Alert';

export default defineComponent({
  name: 'AlertBanner',
  props: {
    alert: {
      type: Object as PropType<AlertModel>,
      default: () => ({
        id: '',
        title: '',
        body: '',
        link: {
          url: '',
          text: ''
        },
        startTime: undefined,
        endTime: undefined
      })
    }
  },
  computed: {
    ...mapGetters('dismissedItems', {
      isDismissedInSession: 'isDismissedInSession'
    }),
    isVisible(): boolean {
      const isDismissed = this.isDismissedInSession(this.alert.id);

      const now = new Date();
      const startTime = this.alert.startTime;
      const endTime = this.alert.endTime;

      let shouldDisplay = true;

      if (startTime) shouldDisplay = shouldDisplay && startTime <= now;

      if (endTime) shouldDisplay = shouldDisplay && now < endTime;

      return shouldDisplay && !isDismissed;
    }
  },
  methods: {
    closeAlert(id: string): void {
      this.$store.dispatch('dismissedItems/dismissInSession', id);
    }
  }
});
</script>
<style lang="scss" scoped>
@import 'src/styles/all.scss';

.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
  max-height: rem-calc(300);
}
.slide-enter,
.slide-leave-to {
  max-height: 0;
  padding: 0;
  overflow: hidden;
}

#alert-banner {
  margin-bottom: 1px;
  color: $white;
  background-color: $disturber-color;
}

.alert-banner {
  &__content {
    padding-top: rem-calc(16);
    padding-bottom: rem-calc(16);

    @include breakpoint(medium up) {
      align-items: center;
    }

    @include breakpoint(small only) {
      align-items: flex-start;
    }
  }

  &__icon {
    @include breakpoint(medium up) {
      font-size: rem-calc(32);
    }

    @include breakpoint(small only) {
      font-size: rem-calc(24);
    }

    margin-right: rem-calc(16);
  }

  &__body-container {
    flex-wrap: wrap;

    @include breakpoint(medium up) {
      align-items: center;
    }

    @include breakpoint(small only) {
      flex-direction: column;
    }
  }

  &__title,
  &__body {
    margin-bottom: 0;
    color: inherit;
    margin-right: rem-calc(8);
  }

  &__link {
    color: $primary-color;
  }

  &__close-button {
    padding-top: rem-calc(16);
    &:hover {
      color: $secondary-color;
    }
  }
}
</style>
